// Does this currency category name look like a quantity rather than an
// amount of money?
//
// Run: deno test --allow-read src/features/pnlAnalytics/currencyCategoryNaming.test.ts
// Run: npm run verify:pnl-currency-category-naming
//
// WHY. Every entry in app_settings.pnl_expense_categories is a dollar line:
// the V1 engine sums it into a cost and subtracts it from profit, and there
// is no unit to say otherwise. On 8 September 2026 two categories called
// "Ordinary Hours" and "Overtime Hours" were created there, which is how
// hours came to be treated as dollars. The right home for those had just
// been built and was in the same dialog: the operational metrics section,
// which writes a pnl_metric_registry row with a real unit and
// financial_inclusion = false.
//
// Nothing here blocks anything. A name is a weak signal and a hard block on
// one would be wrong: "Overtime allowance" is a genuine dollar line whose
// name mentions overtime. This only offers the sentence a user needs at the
// moment they are about to make the mistake, and lets them ignore it.
//
// WHAT IT MUST NEVER DO is fire on a category the organisation already has.
// currencyCategoryNaming.test.ts asserts that against all fifteen live
// production names, so a future addition to the list below cannot quietly
// start nagging about "Handrails & Stairs".

/**
 * Word-boundary patterns, not substrings. "Labour Hire" must not match on
 * "hour", and "Materials" must not match on "rate".
 */
const QUANTITY_WORD_PATTERNS: RegExp[] = [
  /\bhours?\b/i,
  /\bhrs?\b/i,
  /\bdays?\b/i,
  /\bweeks?\b/i,
  /\bcount\b/i,
  /\bheadcount\b/i,
  /\bqty\b/i,
  /\bquantit(?:y|ies)\b/i,
  /\bpercent(?:age)?\b/i,
  /\bratio\b/i,
  /\bper\s+hour\b/i,
  /\bper\s+day\b/i,
];

/** A trailing or standalone percent sign, which no dollar line should carry. */
const PERCENT_SIGN = /%/;

/**
 * True when the name reads like a quantity (hours, counts, percentages)
 * rather than an amount of money.
 */
export function looksLikeQuantityName(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length === 0) return false;
  if (PERCENT_SIGN.test(trimmed)) return true;
  return QUANTITY_WORD_PATTERNS.some((pattern) => pattern.test(trimmed));
}

/**
 * The sentence shown under the name field when the heuristic fires. It names
 * the consequence first, then where to go instead, because the consequence
 * is the part a user cannot see for themselves.
 */
export const QUANTITY_NAME_HINT =
  "Categories here are dollar amounts and are subtracted from profit. " +
  "If this is hours, a count or a percentage, add it under Operational metrics below instead, where it keeps its unit and stays out of the P&L totals.";
